package com.example.mystybag.appCore.views

import androidx.compose.foundation.clickable
import androidx.compose.foundation.Image
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.HorizontalDivider
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalSoftwareKeyboardController
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.arkivanov.decompose.extensions.compose.subscribeAsState
import com.example.mystybag.appCore.logic.PhoneSelectionBottomSheetViewModel
import com.example.mystybag.common.extensions.keyboardPadding
import com.example.mystybag.common.resources.SR
import com.example.mystybag.common.ui.components.custom.DefaultCircleShapedTextField
import com.example.mystybag.common.ui.components.native.DefaultButton
import com.example.mystybag.common.ui.components.native.Texts
import com.example.mystybag.common.views.CountryCodePickerBottomSheet
import com.example.mystybag.common.views.WebDialogCloseButton
import com.example.mystybag.core.expected.LogHelper
import com.example.mystybag.core.expected.PlatformHelper
import com.example.mystybag.core.expected.sharedString
import com.example.mystybag.core.resources.SRC
import com.example.mystybag.core.ui.theme.LocalTheme.p16
import com.example.mystybag.core.ui.theme.LocalTheme.p24
import com.example.mystybag.core.ui.theme.LocalTheme.p32
import com.example.mystybag.core.ui.theme.LocalTheme.p48
import com.example.mystybag.core.ui.theme.LocalTheme.p8
import com.example.mystybag.core.views.AppTheme
import androidx.compose.foundation.text.KeyboardOptions
import dev.icerock.moko.resources.compose.painterResource

@Composable
fun PhoneSelectionBottomSheetView(viewModel: PhoneSelectionBottomSheetViewModel) {
    val state = viewModel.uiState.subscribeAsState()
    val keyboardController = LocalSoftwareKeyboardController.current

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(horizontal = p16)
            .verticalScroll(rememberScrollState())
            .keyboardPadding()
    ) {
        Box(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = p16)
        ) {
            Texts.TitleLarge(
                text = sharedString(SR.strings.mobile_number),
                textAlign = TextAlign.Center,
                color = AppTheme.current.colors.onSurface,
                modifier = Modifier.align(Alignment.Center)
            )
            if (PlatformHelper.isWeb) {
                WebDialogCloseButton(
                    modifier = Modifier
                        .align(Alignment.CenterEnd)
                        .size(40.dp),
                    debugName = "PhoneSelectionBottomSheetView",
                    onClick = {
                        LogHelper.logEvent("[WebDialogCloseButton] PhoneSelectionBottomSheetView parentCloseBottomSheet requested")
                        viewModel.parentCloseBottomSheet()
                    }
                )
            }
        }
        HorizontalDivider(
            modifier = Modifier.padding(top = p16),
            color = AppTheme.current.colors.outlineVariant
        )

        if (state.value.isLoading) {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p32),
                horizontalArrangement = Arrangement.Center
            ) {
                CircularProgressIndicator(
                    modifier = Modifier.size(p48),
                    color = AppTheme.current.colors.primary
                )
            }
        } else {
            Texts.BodyMedium(
                text = sharedString(SR.strings.change_mobile_number_verification_info),
                textAlign = TextAlign.Start,
                color = AppTheme.current.colors.onSurfaceVariant,
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p32)
            )

            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(top = p24),
                horizontalArrangement = Arrangement.spacedBy(p8)
            ) {
                Column(
                    modifier = Modifier
                        .weight(0.3f)
                        .clickable {
                            keyboardController?.hide()
                            viewModel.showCountryPicker()
                        }
                ) {
                    DefaultCircleShapedTextField(
                        value = state.value.dialCode,
                        onValueChange = {},
                        label = sharedString(SR.strings.dial_code),
                        modifier = Modifier.fillMaxWidth(),
                        readOnly = true,
                        enabled = true,
                        canFocus = false,
                        backgroundColor = AppTheme.current.colors.surface,
                        borderColor = AppTheme.current.colors.outline,
                        trailingIcon = {
                            Image(
                                painter = painterResource(
                                    when {
                                        state.value.showCountryPicker -> SRC.images.combobox_arrow_down_green
                                        state.value.dialCode.isNotEmpty() -> SRC.images.combobox_arrow_right_green
                                        else -> SRC.images.combobox_arrow_right_gray
                                    }
                                ),
                                contentDescription = null,
                                contentScale = ContentScale.Fit,
                                modifier = Modifier.size(p16)
                            )
                        }
                    )
                }
                DefaultCircleShapedTextField(
                    value = state.value.phoneNumber,
                    onValueChange = viewModel::onPhoneNumberChanged,
                    label = sharedString(SR.strings.phone_number),
                    modifier = Modifier.weight(0.7f),
                    keyboardOptions = KeyboardOptions(keyboardType = KeyboardType.Phone)
                )
            }
        }

        DefaultButton(
            modifier = Modifier
                .fillMaxWidth()
                .padding(top = p24, bottom = p32),
            caption = sharedString(SR.strings.save),
            callback = {
                keyboardController?.hide()
                viewModel.onSaveClicked()
            }
        )
    }

    CountryCodePickerBottomSheet(
        isVisible = state.value.showCountryPicker,
        countries = state.value.countries,
        selectedDialCode = state.value.dialCode,
        onCountrySelected = { country ->
            viewModel.selectDialCodeCountry(country.dialCode)
        },
        onDismiss = viewModel::hideCountryPicker
    )
}
